/* eslint-disable no-unused-vars */

import { PortableText } from "@portabletext/react";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useUIStateStore } from "@/store/uiStateStore";

import {
  useGetMyAccount,
  useGetMyAccounts,
  // useGetMyProfile,
  useGetSpotifyCount,
  useGetTopGigs,
  useGetTopSongs,
  useGetTopWriters,
} from "../../../api/queries";
import useSiteConfigAuth from "../../../api/sanity/queries/getSiteConfigWithCode";
// import useSiteConfigUnAuth from "../../../api/sanity/queries/getSiteConfigWithDomain";
import { hasAdminRights } from "../../../auth/shared";
import ButtonDashboard from "../../../components/ButtonDashboard";
import ChooseAccountModal from "../../../components/ChooseAccountModal";
import DashboardOpportunitiesBlock from "../../../components/DashboardOpportunitiesBlock";
import H1 from "../../../components/H1";
import H2 from "../../../components/H2";
import LoadingSmall from "../../../components/LoadingSmall";
import Spacer from "../../../components/Spacer";
import TopGigs from "../../../components/TopGigs";
import TopSongs from "../../../components/TopSongs";
import TopWriters from "../../../components/TopWriters";
import useGetColours from "../../../helpers/getColours";
import GigClaimIconDark from "../../../images/rebrandIcons/GigClaimIconDark.png";
import SongIconDark from "../../../images/rebrandIcons/SongIconDark.png";
import WriterIconDark from "../../../images/rebrandIcons/WriterIconDark.png";
// import AppState from "../../../store/appstate";
import useAppStore from "../../../store/appStateZustand";

function Dashboard() {
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const flags = useFlags();
  const disableTopGigs = flags["pd-disable-top-gigs"];

  const myAccounts = useGetMyAccounts();

  const { data: siteConfig } = useSiteConfigAuth();
  // const { data: domainConfig } = useSiteConfigUnAuth();
  // const supportEmail = domainConfig?.support_email;
  const disableDashboard = siteConfig?.disable_dashboard;
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const isRoughTrade = siteConfig?.display_name?.includes(
    "Rough Trade Publishing",
  );

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
    accountname: selectedAccount,
  });

  const { highlightColour2 } = useGetColours(siteConfig);

  const adminRights = hasAdminRights(myAccount?.data);

  const topSongs = useGetTopSongs({ accounts: myAccounts?.data?.artistaccounts });
  const topWriters = useGetTopWriters({
    accounts: myAccounts?.data?.artistaccounts,
  });
  const topGigs = useGetTopGigs({ accounts: myAccounts?.data?.artistaccounts });

  const pendingSpotifyCount = useGetSpotifyCount({
    accounts: myAccounts?.data?.artistaccounts,
  });
  const history = useHistory();
  const [addGigModal, setAddGigModal] = useState(false);
  const [addStatementsModal, setAddStatementsModal] = useState(false);
  const [addSongModal, setAddSongModal] = useState(false);
  const [addWriterModal, setAddWriterModal] = useState(false);

  const [addOpportunityModal, setAddOpportunityModal] = useState(false);
  const [addOportunityUrl, _setAddOportunityUrl] = useState(null);

  const [getSongs, setGetSongs] = useState(false);
  const [getWriters, setGetWriters] = useState(false);
  const [getGigs, setGetGigs] = useState(false);
  const [getSpotify, setGetSpotify] = useState(false);
  const { t } = useTranslation("dashboard");
  const viewStatements = () => {
    history.push("/statements");
  };

  useEffect(() => {
    if (selectedAccount) {
      myAccount.refetch();
    }
  }, [selectedAccount, myAccount]);

  useEffect(() => {
    const timeouts = {
      spotify: null,
      songs: null,
      writers: null,
      gigs: null,
    };

    if (myAccounts.isSuccess) {
      if (!getSpotify) {
        timeouts.spotify = setTimeout(() => {
          setGetSpotify(true);
        }, 2000);
      }
      if (!getSongs) {
        timeouts.songs = setTimeout(() => {
          setGetSongs(true);
        }, 4000);
      }
      if (!getWriters) {
        timeouts.writers = setTimeout(() => {
          setGetWriters(true);
        }, 6000);
      }
      if (!getGigs) {
        timeouts.gigs = setTimeout(() => {
          setGetGigs(true);
        }, 8000);
      }
    }

    return () => {
      clearTimeout(timeouts.spotify);
      clearTimeout(timeouts.songs);
      clearTimeout(timeouts.writers);
      clearTimeout(timeouts.gigs);
    };
  }, [myAccounts.isSuccess, getSpotify, getSongs, getWriters, getGigs]);

  useEffect(() => {
    if (getGigs && !disableTopGigs) {
      topGigs.refetch();
    }
  }, [getGigs, disableTopGigs, topGigs]);

  useEffect(() => {
    if (getSongs) {
      topSongs.refetch();
    }
  }, [getSongs, topSongs]);

  useEffect(() => {
    if (getWriters) {
      topWriters.refetch();
    }
  }, [getWriters, topWriters]);

  useEffect(() => {
    if (getSpotify) {
      pendingSpotifyCount.refetch();
    }
  }, [getSpotify, pendingSpotifyCount]);

  useEffect(() => {
    if (isRoughTrade) {
      history.push("/statements");
    }
  }, [isRoughTrade, history]);

  useEffect(() => {
    if (disableDashboard && !isRoughTrade) {
      history.push("/songs");
    }
  }, [disableDashboard, history, isRoughTrade]);

  // const profile = useGetMyProfile();
  // const hideRoyalties = profile?.data?.hideroyalties || myAccount?.data?.hideroyalties;

  return (
    <>
      {!!addGigModal && (
        <ChooseAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          title={t("dashboard:choose-account-gigs-modal-header")}
          to="/gigs/claim"
          setAccountChoiceModal={setAddGigModal}
          nonAdminTo="/gigs/"
        />
      )}
      {!!addStatementsModal && (
        <ChooseAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          title=""
          to="/statements"
          setAccountChoiceModal={setAddStatementsModal}
          nonAdminTo="/statements/"
        />
      )}
      {!!addOpportunityModal && addOportunityUrl && (
        <ChooseAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          title={t("dashboard:choose-account-modal-header")}
          to={addOportunityUrl}
          nonAdminTo="/opportunities/"
          setAccountChoiceModal={setAddOpportunityModal}
        />
      )}
      {!!addWriterModal && (
        <ChooseAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          title={t("dashboard:choose-account-writer-modal-header")}
          to="/writers/create"
          nonAdminTo="/writers/"
          setAccountChoiceModal={setAddWriterModal}
        />
      )}
      {!!addSongModal && (
        <ChooseAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          title={t("dashboard:choose-account-songs-modal-header")}
          to="/songs/create"
          nonAdminTo="/songs/"
          setAccountChoiceModal={setAddSongModal}
        />
      )}

      <div className="flex flex-wrap">
        <div className="w-full lg:min-h-screen-minus-header">
          <div className="mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40">
            <H1>
              {t("dashboard:welcome-back")}
              {", "}
              {myAccount.data?.accountname}
              !
            </H1>
            <Spacer />
            <div className="flex flex-row flex-wrap justify-between gap-0 font-semibold lg:flex-nowrap lg:gap-6">
              <div
                role="button"
                onClick={() =>
                  void history.push("/songs")}
                style={{
                  borderRadius: "10px 10px 10px 10px",
                  backgroundColor: !isTunecore ? highlightColour2 : undefined,
                }}
                className={`${
                  isTunecore
                    ? "bg-tunecore-dark-element text-white"
                    : "text-old-sentric-table-text"
                } hover:shadow-lg lg:w-1/3 w-full h-44 flex flex-col justify-center gap-4 px-6 mb-6 ease-linear transition-all duration-150 hover:scale-105`}
              >
                <div className="flex font-semibold text-center rounded">
                  {pendingSpotifyCount?.isSuccess
                    && pendingSpotifyCount?.data && (
                    <div>
                      <p className="text-3xl xl:text-4xl">
                        {pendingSpotifyCount.data?.[0]?.count}
                      </p>
                    </div>
                  )}
                  {pendingSpotifyCount?.isLoading && <LoadingSmall />}
                  {!pendingSpotifyCount?.isLoading
                    && !pendingSpotifyCount?.data && <span>-</span>}
                </div>
                <p className="text-lg font-medium text-left xl:text-xl">
                  {/* izzy's code */}
                  {t("dashboard:song-suggestions")}
                </p>
              </div>
              <div
                role="button"
                onClick={() =>
                  void history.push("/writers")}
                style={{
                  borderRadius: "10px 10px 10px 10px",
                  backgroundColor: !isTunecore ? highlightColour2 : undefined,
                }}
                className={`${
                  isTunecore
                    ? "bg-tunecore-dark-element text-white"
                    : "text-old-sentric-table-text"
                } hover:shadow-lg lg:w-1/3 w-full h-44 flex flex-col justify-center gap-4 px-6 mb-6 ease-linear transition-all duration-150 hover:scale-105`}
              >
                <div className="flex font-semibold text-center rounded">
                  {topWriters?.isSuccess && topWriters?.data && (
                    <span className="text-3xl xl:text-4xl">
                      {topWriters.data?.[0]?.verifieddate
                        ? format(
                            Date.parse(topWriters.data?.[0]?.verifieddate),
                            "yyyy-MM-dd",
                          )
                        : "-"}
                    </span>
                  )}

                  {topWriters?.isLoading && <LoadingSmall />}

                  {!topWriters?.isLoading && !topWriters?.data && (
                    <span>-</span>
                  )}
                </div>
                <p className="text-lg font-medium text-left xl:text-xl">
                  {t("dashboard:last-writer-added")}
                </p>
              </div>
              <div
                role="button"
                onClick={() =>
                  void history.push("/songs")}
                style={{
                  borderRadius: "10px 10px 10px 10px",
                  backgroundColor: !isTunecore ? highlightColour2 : undefined,
                }}
                className={`${
                  isTunecore
                    ? "bg-tunecore-dark-element text-white"
                    : "text-old-sentric-table-text"
                } hover:shadow-lg lg:w-1/3 w-full h-44 flex flex-col justify-center gap-4 px-6 mb-6 ease-linear transition-all duration-150 hover:scale-105`}
              >
                <div className="flex font-semibold text-center rounded">
                  {topSongs?.isSuccess && topSongs?.data && (
                    <span className="text-3xl xl:text-4xl">
                      {topSongs.data?.[0]?.createddate
                        ? format(
                            Date.parse(topSongs.data?.[0]?.createddate),
                            "yyyy-MM-dd",
                          )
                        : "-"}
                    </span>
                  )}

                  {topSongs?.isLoading && <LoadingSmall />}
                  {!topSongs?.isLoading && !topSongs?.data && <span>-</span>}
                </div>
                <p className="text-lg font-medium text-left xl:text-xl ">
                  {t("dashboard:last-song-added")}
                </p>
              </div>
            </div>

            <div
              style={{ borderRadius: "10px 10px 10px 10px" }}
              className="flex flex-row flex-wrap justify-between gap-0 lg:flex-nowrap lg:gap-6"
            >
              <div className="w-full lg:w-1/3">
                <ButtonDashboard
                  id="btnDashboardAddSong"
                  onClick={() => setAddSongModal(true)}
                  disabled={!adminRights}
                >
                  {!isTunecore ? (
                    <div className="flex items-center ml-6">
                      <img
                        className="h-4 mr-3"
                        alt="song icon"
                        src={SongIconDark}
                      />
                      {t("dashboard:add-new-song")}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-sm tracking-wider font-tunecore">
                      {t("dashboard:add-new-song")}
                    </div>
                  )}
                </ButtonDashboard>
              </div>
              <div className="w-full lg:w-1/3">
                <ButtonDashboard
                  id="btnDashboardAddWriter"
                  onClick={() => setAddWriterModal(true)}
                  disabled={!adminRights}
                >
                  {!isTunecore ? (
                    <div className="flex items-center ml-6">
                      <img
                        className="h-4 mr-3"
                        alt="writer icon"
                        src={WriterIconDark}
                      />
                      {t("dashboard:add-new-writer")}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-sm tracking-wider font-tunecore">
                      {t("dashboard:add-new-writer")}
                    </div>
                  )}
                </ButtonDashboard>
              </div>
              <div className="w-full lg:w-1/3">
                {!isTunecore ? (
                  <ButtonDashboard
                    id="btnDashboardAddClaim"
                    onClick={() => setAddGigModal(true)}
                    disabled={!adminRights}
                  >
                    <div className="flex items-center ml-6">
                      <img
                        className="h-4 mr-3"
                        alt="gig icon"
                        src={GigClaimIconDark}
                      />
                      {t("dashboard:create-new-gig-claim")}
                    </div>
                  </ButtonDashboard>
                ) : (
                  <ButtonDashboard
                    id="btnDashboardViewStatments"
                    onClick={viewStatements}
                    disabled={!adminRights}
                  >
                    <div className="flex items-center justify-center text-sm tracking-wider font-tunecore">
                      {t("dashboard:view-statements")}
                    </div>
                  </ButtonDashboard>
                )}
              </div>
            </div>

            {!siteConfig?.disable_syncs_and_opportunities_on_dashboard
              && selectedAccount
              && myAccount?.data && (
              <>
                <Spacer />
                <Spacer />
                <H2>{t("dashboard:recent-opportunities-header")}</H2>
                <Spacer />
                <DashboardOpportunitiesBlock />
              </>
            )}

            <div className="flex flex-wrap w-full">
              {siteConfig?.dashboard_box_1
                && siteConfig?.dashboard_box_1.dates && (
                <div className="w-full mt-10 grow lg:w-1/2 lg:pr-4">
                  <div className="h-full p-8 px-8 bg-white rounded-lg html-content db-box">
                    <PortableText value={siteConfig?.dashboard_box_1.dates} />
                  </div>
                </div>
              )}
              {siteConfig?.dashboard_box_2
                && siteConfig?.dashboard_box_2.useful_links && (
                <div className="w-full mt-10 grow lg:w-1/2">
                  <div className="h-full p-8 px-8 bg-white rounded-lg html-content db-box">
                    <PortableText
                      value={siteConfig?.dashboard_box_2.useful_links}
                    />
                  </div>
                </div>
              )}
              <Spacer />
              <Spacer />
              <H2>{t("dashboard:activity-overview-header")}</H2>
              <Spacer />
              <TopSongs />
              <Spacer />
              <div className="flex flex-wrap w-full gap-8 lg:flex-nowrap">
                <TopWriters />
                {!isTunecore && !disableTopGigs && <TopGigs />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Dashboard;
